import { findKey, get, isEmpty } from "lodash";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router";
import { getActiveProvider } from "shared/auth/providers";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { SIGN_IN, UPDATE_USER } from "utils/api/graphql/mutations/users";
import { SCREENS, USER_TYPES } from "utils/constants";
import { API_ORIGIN } from "utils/helpers/apiOrigin";
import { getAuthDescriptor } from "utils/helpers/authProvider";
import View from "./View";

const SignIn = () => {
  const navigate = useNavigate();
  const { setUser, setAccessToken, accessToken, clearStore } =
    useContext(StoreContext);

  const [checkUser, { loading }] = useLazyQuery(graphql.queries.CHECK_USER, {
    fetchPolicy: "no-cache",
    onCompleted: ({ profile }) => onSignIn(profile),
    onError: () => {},
  });

  const [signIn] = useMutation(SIGN_IN);
  const [updateUser] = useMutation(UPDATE_USER);

  const onSubmit = ({ username, password }) => {
    signIn({
      variables: {
        data: {
          username,
          password,
        },
      },
      onCompleted: ({ signIn }) =>
        setAccessToken({ accessToken: signIn?.accessToken }),
    });
  };

  useEffect(() => {
    if (accessToken) {
      checkUser();
    }
  }, [accessToken]);

  const onSignIn = (user) => {
    if (isEmpty(user)) return;
    if (get(user, "meta.type") && !user.isActive) {
      clearStore();
      return navigate(SCREENS.AUTH.NOT_ACTIVE.path);
    }
    if (get(user, "meta.type") === USER_TYPES.EXTERNAL_ADVISOR.type) {
      return navigate(SCREENS.AUTH.NOT_AUTHORIZED.path);
    }
    if (!user.hasSignedIn) {
      return updateUser({
        variables: { where: { id: user.id }, data: { hasSignedIn: true } },
        onCompleted: () => {
          setUser({ user: { ...user, hasSignedIn: true } });
          navigate(
            USER_TYPES[
              findKey(
                USER_TYPES,
                (element) => element.type === user?.meta?.type,
              )
            ]?.index,
          );
        },
      });
    }
    get(user, "meta.type") && setUser({ user });
    get(user, "meta.type") &&
      navigate(
        USER_TYPES[
          findKey(
            USER_TYPES,
            (element) => element.type === get(user, "meta.type"),
          )
        ]?.index,
      );
  };

  const onLinkClick = () => navigate(SCREENS.AUTH.FORGOT_PASSWORD.path);

  const onZitadelLogin = () => {
    const { paths } = getAuthDescriptor();
    const loginPath = (paths && paths.login) || "/api/auth/zitadel/login";
    // Use the live browser origin (not REACT_APP_BASE_URL) so the callback
    // returns to the exact front-end the user started from (ngrok, alternate
    // org domains, etc.).
    const returnUrl = window.location.origin;
    const params = `?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = `${API_ORIGIN}${loginPath}${params}`;
  };

  const provider = getActiveProvider();

  if (loading) return <Loading />;
  return (
    <View
      provider={provider}
      onSubmit={onSubmit}
      onLinkClick={onLinkClick}
      onZitadelLogin={onZitadelLogin}
    />
  );
};

export default SignIn;
